import { useState } from "react";
import { Button, Field, Input, Tab, TabList } from "@fluentui/react-components";
import { showToast } from "#root/components/GlobalToaster";
import { Incident, useInfiniteIncidents, useIncidentById } from "#root/queries/incidents";
import Paginator from "#root/components/Paginator";
import LoadingWrapper from "#root/components/LoadingWrapper";
import "./styles.scss";
import InfiniteDropdown from "#root/components/InfiniteDropdown";
import { DialogBox } from "#root/components/DialogBox";
import { useInfiniteDropdown } from "#root/hooks/useInfiniteDropdown";
import { useVTrackerSelectedIncident } from "#root/queries/local";
import { Add16Filled, CheckmarkRegular, Delete20Filled, DeleteRegular, DismissRegular, Edit20Filled, MailInboxRegular } from "@fluentui/react-icons";
import { useHotWash, HotWash, useCreateHotWash, useUpdateHotWash, useBatchDeleteHotWash, HotWashType } from "#root/queries/hotWash";
import { Breadcrumbs } from "#root/components/Breadcrumbs";
import { DataGrid, DataGridBody, DataGridCell, DataGridRow, TableColumnDefinition, createTableColumn } from "@fluentui/react-table";

interface HotWashSectionProps {
  title: string;
  type: HotWashType;
  selectedItems: Set<string>;
  onSelectionChange: (type: HotWashType, selectedItems: Set<string>) => void;
  selectedIncidentId: string | null;
}

const HotWashSection: React.FC<HotWashSectionProps> = ({
  title,
  type,
  selectedItems,
  onSelectionChange,
  selectedIncidentId
}) => {
  const itemsPerPage = 10;
  const [page, setPage] = useState(1);
  const [own, setOwn] = useState(true);
  const [editing, setEditing] = useState<{ item: HotWash | null; values: HotWash }>({
    item: null,
    values: {}
  });
  const [dialogs, setDialogs] = useState<{
    create: { open: boolean; values: HotWash };
    delete: { open: boolean; item: HotWash | null };
  }>({
    create: { open: false, values: { name: '' } },
    delete: { open: false, item: null }
  });

  const {
    data: response,
    isLoading
  } = useHotWash({
    skip: (page - 1) * itemsPerPage,
    take: itemsPerPage,
    type,
    own,
    incidentId: selectedIncidentId || undefined
  });

  const createHotWashMutation = useCreateHotWash();
  const updateHotWashMutation = useUpdateHotWash();
  const batchDeleteHotWashMutation = useBatchDeleteHotWash();

  const items = response?.items || [];
  const totalItems = response?.totalCount || 0;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    onSelectionChange(type, new Set());
  };

  const handleOwnChange = (newOwn: boolean) => {
    setOwn(newOwn);
    onSelectionChange(type, new Set()); 
    setEditing({ item: null, values: {} });
  };

  const onSelectionChangeInternal = (event: React.MouseEvent | React.KeyboardEvent, data: any) => {
    if ((event.target as HTMLElement).closest('[type="checkbox"]')) {
      const selectedIds = new Set<string>(
        Array.from(data.selectedItems as Set<number>)
          .map(index => items[index]?.id)
          .filter(Boolean) as string[]
      );
      onSelectionChange(type, selectedIds);
    }
  };

  // Handle edit

  const handleEdit = (item: HotWash) => {
    setEditing({ item, values: item });
  };

  const handleEditCancel = () => {
    setEditing({ item: null, values: {} });
  };

  const handleEditConfirm = async () => {
    const { item, values } = editing;
    if (!values.name?.trim()) {
      showToast('Please enter a name', 'error');
      return;
    }

    if (item?.id && values) {
      try {
        await updateHotWashMutation.mutateAsync({
          hotWash: values
        });
        showToast('Hot Wash item updated successfully', 'success');
        setEditing({ item: null, values: {} });
      } catch (error) {
        showToast('Failed to update Hot Wash item', 'error');
      }
    }
  };

  const handleEditedValueChange = (value: string) => {
    setEditing(prev => ({
      ...prev,
      values: { ...prev.values, name: value }
    }));
  };

  // Handle create

  const handleCreateClick = () => {
    setDialogs(prev => ({
      ...prev,
      create: { open: true, values: { name: '' } }
    }));
  };

  const handleCreateHotWash = async () => {
    const { values } = dialogs.create;
    if (!values.name?.trim()) {
      showToast('Please enter a name', 'error');
      return;
    }

    try {
      await createHotWashMutation.mutateAsync({
        hotWash: { ...values, type, incidentId: selectedIncidentId! }
      });
      setDialogs(prev => ({
        ...prev,
        create: { open: false, values: { name: '' } }
      }));
      showToast('Hot Wash item created successfully', 'success');
    } catch (error) {
      showToast('Failed to create Hot Wash item', 'error');
    }
  };

  // Handle delete

  const handleDelete = (item: HotWash) => {
    setDialogs(prev => ({
      ...prev,
      delete: { open: true, item }
    }));
  };

  const handleConfirmDelete = async () => {
    try {
      await batchDeleteHotWashMutation.mutateAsync({
        ids: dialogs.delete.item?.id ? [dialogs.delete.item.id] : []
      });
      setDialogs(prev => ({
        ...prev,
        delete: { open: false, item: null }
      }));
      onSelectionChange(type, new Set());
      showToast(`${dialogs.delete.item ? 'Hot Wash item' : 'Hot Wash items'} deleted successfully`, 'success');
    } catch (error) {
      showToast('Failed to delete Hot Wash item(s)', 'error');
    }
  };

  const columns: TableColumnDefinition<HotWash>[] = [
    createTableColumn<HotWash>({
      columnId: "name",
      renderHeaderCell: () => "Name",
      renderCell: (item) => (
        editing.item?.id === item.id && own ? (
          <Input
            required
            appearance="underline"
            value={editing.values.name || ''}
            onChange={(_, data) => handleEditedValueChange(data.value)}
            disabled={updateHotWashMutation.isPending}
          />
        ) : (
          <div title={item.name}>{item.name}</div>
        )
      ),
    }),
    ...(own ? [
      createTableColumn<HotWash>({
        columnId: "actions",
        renderHeaderCell: () => "Actions",
        renderCell: (item) => (
          editing.item?.id === item.id ? (
            <>
              <Button
                appearance="primary"
                icon={<CheckmarkRegular />}
                onClick={handleEditConfirm}
                disabled={updateHotWashMutation.isPending}
              />
              <Button
                appearance="secondary"
                icon={<DismissRegular />}
                onClick={handleEditCancel}
                disabled={updateHotWashMutation.isPending}
              />
            </>
          ) : (
            <>
              <Button
                title={`Edit ${type}`}
                appearance="subtle"
                icon={<Edit20Filled />}
                onClick={() => handleEdit(item)}
                disabled={updateHotWashMutation.isPending}
              />
              <Button
                title={`Delete ${type}`}
                appearance="subtle"
                icon={<Delete20Filled />}
                onClick={() => handleDelete(item)}
              />
            </>
          )
        ),
      })
    ] : [])
  ];

  return (
    <section className="hot-wash-section">
      <div className="hot-wash-section__header">
        <h3 className="hot-wash-section__title">{title}</h3>
        <div className="hot-wash-section__divider divider"></div>
        <TabList className="hot-wash-section__tab-list"
          selectedValue={own.toString()}
          onTabSelect={(_, data) => handleOwnChange(data.value === 'true')}
        >
          <Tab value="true">My Items</Tab>
          <Tab value="false">View All</Tab>
        </TabList>
        <Button
          appearance="primary"
          className="hot-wash-section__button"
          icon={<Add16Filled />}
          onClick={handleCreateClick}
          disabled={own === false}
        >
          {type}
        </Button>
      </div>
      <LoadingWrapper
        isLoading={isLoading}
        isEmpty={items.length === 0 || !selectedIncidentId}
        emptyMessage={!selectedIncidentId ? 'Please select an incident' : `No ${title.toLowerCase()} found`}
        emptyIcon={<MailInboxRegular />}
      >
        <div className="emc-grid">
          <DataGrid
            className="emc-datagrid"
            items={items}
            columns={columns}
            selectionMode={own ? "multiselect" : undefined}
            selectedItems={new Set(
              items.reduce<number[]>((acc, item, index) => 
                selectedItems.has(item.id!) ? [...acc, index] : acc, [])
            )}
            onSelectionChange={onSelectionChangeInternal}
          >
            <DataGridBody<HotWash>>
              {({ item, rowId }) => (
                <DataGridRow<HotWash>
                  key={rowId}
                  selectionCell={own ? { "aria-label": "Select row" } : undefined}
                  className={editing.item?.id === item.id ? 'editing' : ''}
                >
                  {({ renderCell, columnId }) => (
                    <DataGridCell key={columnId} className={columnId.toString()}>
                      {renderCell(item)}
                    </DataGridCell>
                  )}
                </DataGridRow>
              )}
            </DataGridBody>
          </DataGrid>
        </div>

        <Paginator
          currentPage={page}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      </LoadingWrapper>

      <DialogBox
        open={dialogs.create.open}
        onOpenChange={(open) => setDialogs(prev => ({
          ...prev,
          create: { ...prev.create, open }
        }))}
        title={`Create ${type}`}
        primaryButtonText="Create"
        onPrimaryButtonClick={handleCreateHotWash}
        isPrimaryButtonLoading={createHotWashMutation.isPending}
      >
        <Field label={`Description of ${type}`} required>
          <Input
            value={dialogs.create.values.name || ''}
            onChange={(_, data) => setDialogs(prev => ({
              ...prev,
              create: {
                ...prev.create,
                values: { name: data.value }
              }
            }))}
            disabled={createHotWashMutation.isPending}
            required
          />
        </Field>
      </DialogBox>

      <DialogBox
        open={dialogs.delete.open}
        onOpenChange={(open) => setDialogs(prev => ({
          ...prev,
          delete: { ...prev.delete, open }
        }))}
        title="Delete Hot Wash"
        primaryButtonText="Delete"
        onPrimaryButtonClick={handleConfirmDelete}
        isPrimaryButtonLoading={batchDeleteHotWashMutation.isPending}
      >
        Are you sure you want to delete Hot Wash item {dialogs.delete.item?.name}?
      </DialogBox>
    </section>
  );
};

const HotWashPage: React.FC = () => {
  const { selectedIncidentId: vTrackerIncidentId, setVTrackerSelectedIncidentId } = useVTrackerSelectedIncident();
  const [selections, setSelections] = useState<{ [key in HotWashType]: Set<string> }>({
    Strength: new Set(),
    Improvement: new Set()
  });

  const [multiDeleteDialog, setMultiDeleteDialog] = useState<{ open: boolean; items: string[] }>({open: false, items: []});

  const {
    selectedId: selectedIncidentId,
    selectedData: selectedIncidentData,
    handleChange: handleIncidentChange,
    setFilter: setIncidentFilter,
    queryResult: incidentQueryResult
  } = useInfiniteDropdown<Incident>({
    initialId: vTrackerIncidentId || '',
    onChange: (id) => setVTrackerSelectedIncidentId(id),
    useInfiniteQuery: useInfiniteIncidents,
    useItemById: useIncidentById
  });

  const batchDeleteHotWashMutation = useBatchDeleteHotWash();

  const handleSelectionChange = (type: HotWashType, selectedIds: Set<string>) => {
    setSelections(prev => ({
      ...prev,
      [type]: selectedIds
    }));
  };

  const handleDeleteMultipleClick = () => {
    const allSelectedIds = [
      ...Array.from(selections.Strength),
      ...Array.from(selections.Improvement)
    ];
    setMultiDeleteDialog({
      open: true,
      items: allSelectedIds
    });
  };

  const handleConfirmMultiDelete = async () => {
    try {
      await batchDeleteHotWashMutation.mutateAsync({ ids: multiDeleteDialog.items });
      setSelections({
        Strength: new Set(),
        Improvement: new Set()
      });
      setMultiDeleteDialog({ open: false, items: [] });
      showToast(`${multiDeleteDialog.items.length > 1 ? 'Hot Wash items' : 'Hot Wash item'} deleted successfully`, 'success');
    } catch (error) {
      showToast('Failed to delete Hot Wash item(s)', 'error');
    }
  };

  const totalSelectedItems = selections.Strength.size + selections.Improvement.size;

  return (
    <div className="hot-wash">
      <div className="emc-page-header">
        <div className="emc-page-header__top-row">
          <div className="emc-page-header__breadcrumbs">
            <Breadcrumbs />
          </div>
          <div className="emc-page-header__props">
            <Field label="Incident">
              <InfiniteDropdown
                value={selectedIncidentId || ''}
                onChange={(_, data) => handleIncidentChange(data.value)}
                onFilterChange={setIncidentFilter}
                queryResult={incidentQueryResult}
                itemToOption={(incident) => ({
                  value: incident.id ?? '',
                  text: incident.name ?? ''
                })}
                placeholder="Select incident"
                selectedItem={selectedIncidentData}
              />
            </Field>
          </div>
        </div>
      </div>

      {}

      {selectedIncidentId ? (
        <>
          <HotWashSection
            title="Strengths"
            type="Strength"
            selectedItems={selections.Strength}
            onSelectionChange={handleSelectionChange}
            selectedIncidentId={selectedIncidentId}
          />
          <HotWashSection
            title="Improvements"
            type="Improvement"
            selectedItems={selections.Improvement}
            onSelectionChange={handleSelectionChange}
            selectedIncidentId={selectedIncidentId}
          />
        </>
      ) : (
        <div className="loading-wrapper loading-wrapper--empty">
          <div className="loading-wrapper__icon"><MailInboxRegular /></div>
          <p>Please select an incident</p>
        </div>
      )}

      {totalSelectedItems > 0 && (
        <div className="emc-page-footer">
          <Button
            appearance="secondary"
            onClick={() => setSelections({
              Strength: new Set(),
              Improvement: new Set()
            })}
          >
            Cancel
          </Button>
          <Button
            appearance="primary"
            icon={<DeleteRegular />}
            onClick={handleDeleteMultipleClick}
          >
            Delete Selected ({totalSelectedItems})
          </Button>
        </div>
      )}

      <DialogBox
        open={multiDeleteDialog.open}
        onOpenChange={(open) => setMultiDeleteDialog(prev => ({ ...prev, open }))}
        title={`Delete ${multiDeleteDialog.items.length} Hot Wash Items`}
        primaryButtonText="Delete"
        onPrimaryButtonClick={handleConfirmMultiDelete}
        isPrimaryButtonLoading={batchDeleteHotWashMutation.isPending}
      >
        Are you sure you want to delete {multiDeleteDialog.items.length} Hot Wash {multiDeleteDialog.items.length > 1 ? 'items' : 'item'}?
      </DialogBox>
    </div>
  );
};

export default HotWashPage;
